/**
 * @file date-picker/subcomponents/calendar-grid.tsx
 *
 * 7-column calendar grid with slide animation during month navigation.
 * Uses AnimatePresence + direction-aware variants for smooth transitions.
 */

import { AnimatePresence, m } from "motion/react";
import * as React from "react";
import { CALENDAR_SLIDE_VARIANTS } from "../../shared/animation-tokens";
import { getWeeksInMonth } from "../../shared/calendar-model";
import type { ResolvedDateLocale, SelectableDates } from "../../shared/types";
import { DayCell } from "./day-cell";
import { WeekdayRow } from "./weekday-row";

interface CalendarGridProps {
	/** UTC ms of 1st day of month being displayed */
	displayedMonthMs: number;
	/** Navigation direction determining slide animation axis */
	slideDirection: "prev" | "next" | null;
	locale: ResolvedDateLocale;
	selectableDates: SelectableDates;
	selectedMs: number | null;
	selectedStartMs?: number | null;
	selectedEndMs?: number | null;
	isRangePicker?: boolean;
	onDaySelect(ms: number): void;
}

/**
 * Calendar grid 6×7 with direction-aware slide animation.
 * Renders WeekdayRow + 6 day rows.
 */

export const CalendarGrid: React.FC<CalendarGridProps> = ({
	displayedMonthMs,
	slideDirection,
	locale,
	selectableDates,
	selectedMs,
	selectedStartMs = null,
	selectedEndMs = null,
	isRangePicker = false,
	onDaySelect,
}) => {
	const d = new Date(displayedMonthMs);
	const year = d.getUTCFullYear();
	const month = d.getUTCMonth();

	const weeks = React.useMemo(
		() => getWeeksInMonth(year, month, locale.weekStartsOn),
		[year, month, locale.weekStartsOn],
	);

	const enterVariant =
		slideDirection === "next" ? "enterFromRight" : "enterFromLeft";
	const exitVariant = slideDirection === "next" ? "exitToLeft" : "exitToRight";

	return (
		<table className="w-full px-3 border-collapse block">
			<thead className="block">
				<WeekdayRow locale={locale} />
			</thead>
			<AnimatePresence initial={false} mode="popLayout">
				<m.tbody
					key={displayedMonthMs}
					variants={CALENDAR_SLIDE_VARIANTS}
					initial={slideDirection !== null ? enterVariant : false}
					animate="center"
					exit={slideDirection !== null ? exitVariant : undefined}
					className="overflow-hidden block"
					aria-label={new Intl.DateTimeFormat(locale.locale, {
						year: "numeric",
						month: "long",
						timeZone: "UTC",
					}).format(new Date(displayedMonthMs))}
				>
					{weeks.map((week, weekIdx) => (
						<tr
							// biome-ignore lint/suspicious/noArrayIndexKey: stable week index per month
							key={weekIdx}
							className="grid grid-cols-7"
						>
							{week.map((day, dayIdx) =>
								day === null ? (
									<td
										// biome-ignore lint/suspicious/noArrayIndexKey: empty cells have no stable domain identifier
										key={`empty-${weekIdx}-${dayIdx}`}
										className="w-10 h-10 mx-auto block"
										aria-hidden
									/>
								) : (
									<DayCell
										key={day.utcMs}
										day={day}
										selectedMs={selectedMs}
										selectedStartMs={selectedStartMs}
										selectedEndMs={selectedEndMs}
										isRangePicker={isRangePicker}
										isSelectable={selectableDates.isSelectableDate(day.utcMs)}
										onSelect={onDaySelect}
										locale={locale.locale}
									/>
								),
							)}
						</tr>
					))}
				</m.tbody>
			</AnimatePresence>
		</table>
	);
};

CalendarGrid.displayName = "CalendarGrid";
